import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * IVExpansionDetector — Safe Buy Zone / Crush Warning
 *
 * How it works:
 * - Compares Live IV of ATM options against the Base Snapshot (09:15/09:16).
 * - If Live IV is significantly higher than Base IV, premiums are expanding, making it a safe momentum buy.
 * - If Live IV is significantly lower despite the spot moving, buyers are at risk of IV Crush.
 */
export const evaluateIVExpansionDetector: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, gap, baseSnapshot } = context;
    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0 || !baseSnapshot) return null;

    const baseRawOpts = baseSnapshot.options || baseSnapshot.optionChain || [];
    const baseFlatOpts = ensureFlatOpts(baseRawOpts);
    
    const liveRawOptsArr = context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);
    if (!liveFlatOpts.length || !baseFlatOpts.length) return null;

    const atmStrike = Math.round(spot / gap) * gap;

    // Check ATM CE and PE
    for (const type of ['CE', 'PE'] as const) {
        const liveOpt = liveFlatOpts.find((o: any) => o.strike === atmStrike && o.type === type);
        const baseOpt = baseFlatOpts.find((o: any) => o.strike === atmStrike && o.type === type);

        if (liveOpt && baseOpt) {
            // Some APIs provide IV directly. If not, we might fall back to premium changes vs intrinsic.
            // Upstox provides `iv` or `impliedVolatility`. Our ensureFlatOpts might need to map it if we strictly use `iv`.
            const liveIV = liveOpt.iv || (liveOpt as any).impliedVolatility || 0;
            const baseIV = baseOpt.iv || (baseOpt as any).impliedVolatility || 0;

            if (liveIV > 0 && baseIV > 0) {
                const ivExpansion = liveIV - baseIV;

                // If IV has expanded by more than 2-3 points from morning base, premiums are pumping.
                if (ivExpansion > 2.0) {
                    return {
                        trade_type: type === 'CE' ? 'BULL' : 'BEAR',
                        strike: atmStrike,
                        option_type: type,
                        entry_price: liveOpt.ltp || liveOpt.price || liveOpt.close || 0,
                        remarks: `SAFE TO BUY: IV Expanded by +${ivExpansion.toFixed(1)}%`
                    };
                }
            }
        }
    }

    return null;
};
